import {
   AfterViewInit,
   ChangeDetectorRef,
   Component,
   OnDestroy,
   OnInit
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { animate, state, style, transition, trigger } from '@angular/animations';

// Packages.
import { Chart, ChartOptions, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Classes.
import { DataTaskChart } from '../../../domain/data-task-chart';
import { TaskReportChart } from '../../../domain/task-report-chart';
import { Property } from '../../../domain/property';
import { TaskOverviewChartOptions } from '../../../domain/task-overview-chart-options';

// Services.
import { PropertyStoreService } from '../../../services/stores/property-store.service';

// Data.
import { ReportStoreService } from '../../../services/stores/report-store.service';
import { ReportRequest } from '../../../services/request/report-request';
import { DateTime } from 'luxon';

const AiStarPlugin = {
   id: 'aiStar',
   afterDraw(chart: any, _args: any, opts: any) {
      const { ctx, chartArea, scales } = chart;
      const yScale = scales?.y;
      if (!yScale) return;

      const indices: number[] = opts?.indices ?? [];
      const color: string = opts?.color ?? '#f78100';
      const offset: number = opts?.offset ?? 10; // distance left of plot area
      const font: string = opts?.font ?? 'bold 14px sans-serif';

      ctx.save();
      ctx.fillStyle = color;
      ctx.font = font;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';

      const x = chartArea.left - offset; // draw in the axis gutter
      for (const i of indices) {
         const y = yScale.getPixelForTick(i);
         if (Number.isFinite(y)) {
            ctx.fillText('✱', x, y);
         }
      }
      ctx.restore();
   }
};

Chart.register(...registerables);
Chart.register(ChartDataLabels);
Chart.register(AiStarPlugin);

@Component({
   selector: 'app-tasks-overview-chart',
   templateUrl: './tasks-overview-chart.component.html',
   styleUrls: ['./tasks-overview-chart.component.scss'],
   animations: [
      trigger('aiResponseAnimation', [
         state(
            'closed',
            style({
               height: '0',
               opacity: 0,
               overflow: 'hidden'
            })
         ),
         state(
            'open',
            style({
               height: '*',
               opacity: 1
            })
         ),
         transition('closed <=> open', [animate('300ms ease-in-out')])
      ])
   ]
})
export class TasksOverviewChartComponent implements OnInit, AfterViewInit, OnDestroy {
   // Classes.
   currentProperty?: Property;
   tasksOverviewForm: FormGroup;

   // Data.
   allTasks: DataTaskChart[];
   data: TaskReportChart;
   selectedTasks: DataTaskChart[];
   taskSelection: boolean = true;
   lastRequest?: ReportRequest;

   // Chart instance.
   chart: Chart | undefined;
   options: ChartOptions<'bar'>;

   // Controls.
   aiResponse: boolean;
   selectedView: any;
   dateRange: string;
   selectedTask: number | null;

   constructor(
      private propStore: PropertyStoreService,
      private cdr: ChangeDetectorRef,
      private _reportStore: ReportStoreService
   ) {
      // Data.
      this.data = new TaskReportChart();
      this.allTasks = [];
      this.selectedTasks = [];
      this.tasksOverviewForm = new FormGroup({
         startDate: new FormControl(new Date(), Validators.required),
         finishDate: new FormControl(new Date(), Validators.required),
         dateRangeOption: new FormControl('', Validators.required)
      });
      this.propStore.currentProperty.subscribe((prop: any) => {
         this.currentProperty = prop;
      });

      // Controls.
      this.aiResponse = false;
      this.selectedView = '';
      this.dateRange = '';
      this.selectedTask = null;

      // Configure chart options.
      this.options = new TaskOverviewChartOptions().options;
   }

   ngOnInit() {
      this.updateChartData();
   }

   ngAfterViewInit() {
      this.initializeChart();
   }

   ngOnDestroy() {
      if (this.chart) {
         this.chart.destroy(); // Destroy the existing chart instance
      }
   }

   initializeChart() {
      const ctx = document.getElementById('tasksOverviewChart') as HTMLCanvasElement;
      if (ctx) {
         // Destroy the previous chart instance if it exists
         if (this.chart) {
            this.chart.destroy();
         }

         // Create a new chart instance
         this.chart = new Chart(ctx, {
            type: 'bar',
            data: this.data,
            options: this.options
         });
      }
   }

   updateChartData() {
      const labels = this.selectedTasks.map((t) => t.name);
      const data = this.selectedTasks.map((t) => t.daysToComplete);

      const aiIndexes = this.selectedTasks
         .map((t, i) => (t.isAiEnabled ? i : -1))
         .filter((i) => i >= 0);

      this.data = {
         labels,
         datasets: [
            {
               label: 'Days to complete',
               backgroundColor: '#ffd8b1',
               borderColor: '#ff9e43',
               borderWidth: 2,
               data
            }
         ]
      };

      const pluginOpts: any = {
         ...(this.options.plugins ?? {}),
         aiStar: {
            indices: aiIndexes,
            color: '#f78100',
            offset: 1,
            font: '10px'
         },
         datalabels: {
            display: (ctx: any) => Number(ctx.dataset.data[ctx.dataIndex] ?? 0) > 0.5, // hide 0
            formatter: (v: number) => `${Number(v).toFixed(2)} days`,
            anchor: 'end',
            align: 'left',
            offset: 6,
            clamp: true,
            clip: false
         }
      };

      this.options = {
         ...this.options,
         indexAxis: 'y',
         plugins: pluginOpts,
         layout: {
            ...(this.options.layout ?? {}),
            padding: {
               ...((this.options.layout as any)?.padding ?? {}),
               left: 12
            }
         },
         scales: {
            ...(this.options.scales ?? {}),
            y: {
               ...((this.options.scales as any)?.y ?? {}),
               ticks: {
                  ...(((this.options.scales as any)?.y ?? {}).ticks ?? {}),
                  padding: 6
               }
            }
         }
      };

      // 5) apply to chart
      if (this.chart) {
         this.chart.data = this.data;
         this.chart.options = this.options; // <-- important!
         this.chart.update();
      } else {
         this.initializeChart();
      }

      // 6) keep your dynamic height
      const ctx = document.getElementById('tasksOverviewChart') as HTMLCanvasElement;
      if (ctx) {
         const minHeight = 60;
         const calculatedHeight = this.selectedTasks.length * 60;
         ctx.style.height = `${Math.max(calculatedHeight, minHeight)}px`;
      }

      this.cdr.detectChanges();
   }

   showTaskSelection() {
      this.taskSelection = !this.taskSelection;
   }

   showAiResponse() {
      this.aiResponse = !this.aiResponse;
   }

   dragStart(event: DragEvent, view: any, from: string) {
      event.dataTransfer?.setData('task', JSON.stringify({ view, from }));
   }

   drop(event: DragEvent, to: string) {
      event.preventDefault();
      const data = event.dataTransfer?.getData('task');
      if (data) {
         const { view, from } = JSON.parse(data);

         if (from === 'allTasks' && to === 'selectedTasks') {
            this.selectedTasks.push(view);
            this.allTasks = this.allTasks.filter((t) => t.name !== view.name);
         } else if (from === 'selectedTasks' && to === 'allTasks') {
            this.allTasks.push(view);
            this.selectedTasks = this.selectedTasks.filter(
               (t) => t.name !== view.name
            );
         }

         this.updateChartData();
      }
   }

   allowDrop(event: DragEvent) {
      event.preventDefault();
   }

   addAllTasks() {
      this.selectedTasks = [...this.selectedTasks, ...this.allTasks];
      this.allTasks = [];
      this.updateChartData();
   }

   resetChart() {
      this.allTasks = [...this.allTasks, ...this.selectedTasks];
      this.selectedTasks = [];
      this.updateChartData();
   }

   onTaskChange(event: any) {
      this.selectedTask = event.value.task_id;
      this.getChartData();
   }

   onDateRangeChange(range: any) {
      this.dateRange = range;
      this.getChartData();
   }

   getChartData() {
      this.allTasks = [];
      this.selectedTasks = [];
      this.updateChartData();
      this.lastRequest = new ReportRequest();
      switch (this.dateRange) {
         case 'current_month':
            this.lastRequest.start = DateTime.local().startOf('month').toISODate();
            this.lastRequest.finish = DateTime.now().toISODate();
            break;
         case 'last_month':
            this.lastRequest.start = DateTime.now()
               .minus({ months: 1 })
               .startOf('month')
               .toISODate();
            this.lastRequest.finish = DateTime.now()
               .minus({ months: 1 })
               .endOf('month')
               .toISODate();
            break;
         case 'last_week':
            this.lastRequest.start = DateTime.now()
               .minus({ weeks: 1 })
               .startOf('week')
               .toISODate();
            this.lastRequest.finish = DateTime.now()
               .minus({ weeks: 1 })
               .endOf('week')
               .toISODate();
            break;
         case 'today':
         default:
            this.lastRequest.start = DateTime.now().startOf('day').toISODate();
            this.lastRequest.finish = DateTime.now().endOf('day').toISODate();
            break;
      }

      const propId = this.currentProperty?.id ? this.currentProperty.id : 0;

      this._reportStore
         .runTaskQuery(propId, this.lastRequest)
         .subscribe((response) => {
            this.allTasks = response;
         });
   }
}
